import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import JsonLd from "@/components/base/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL;
const logoUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/16c9e81c106f6a445869e4a45760203f.jpeg';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'The Complete Hipobuy Sheet Guide 2026: Master Spreadsheet Shopping Research',
  description: 'Everything you need to know about using Hipobuy Sheet for smarter product research, category navigation, price tracking, and informed buying decisions across 14 product categories.',
  image: 'https://readdy.ai/api/search-image?query=Clean%20modern%20desk%20setup%20with%20laptop%20displaying%20organized%20colorful%20spreadsheet%20data%2C%20surrounded%20by%20fashion%20product%20samples%20including%20sneakers%20and%20fabric%20swatches%2C%20warm%20cream%20and%20beige%20ambient%20tones%2C%20soft%20natural%20window%20lighting%2C%20minimalist%20creative%20workspace%2C%20professional%20yet%20cozy%20atmosphere%2C%20high-end%20editorial%20photography%2C%20thoughtful%20composition%20with%20shallow%20depth%20of%20field%2C%20calm%20productive%20mood&width=1200&height=600&seq=guide-organized-spreadsheet-2026&orientation=landscape',
  datePublished: '2026-07-01T00:00:00+00:00',
  dateModified: '2026-07-11T00:00:00+00:00',
  author: {
    '@type': 'Organization',
    name: 'Hipobuy Sheet Guide',
    url: siteUrl,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Hipobuy Sheet Guide',
    logo: {
      '@type': 'ImageObject',
      url: logoUrl,
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}/hipobuy-sheet-guide`,
  },
};

export default function Article() {
  return (
    <>
      <JsonLd data={articleSchema as unknown as Record<string, unknown>} />
      <Navbar />
      <main className="pt-16">
        <article>
          <header className="bg-background-100 py-14 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 mb-4">
                <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
                  Home
                </Link>
                <span className="text-foreground-300 text-sm">/</span>
                <span className="text-sm text-foreground-500">Hipobuy Sheet Guide</span>
              </div>
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
                Comprehensive Guide
              </span>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-950 leading-tight mb-4">
                The Complete Hipobuy Sheet Guide 2026: Master Spreadsheet Shopping Research
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
                Everything you need to know about using Hipobuy Sheet for smarter product research, category navigation, price tracking, and informed buying decisions across 14 product categories.
              </p>
              <div className="flex items-center gap-4 mt-6 text-xs text-foreground-400">
                <span>Updated July 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>12 min read</span>
              </div>
            </div>
          </header>

          <div className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="prose-custom space-y-6 text-foreground-700 leading-relaxed text-sm md:text-base">

              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mt-10 mb-4">
                Introduction: Why Hipobuy Sheet Matters for Modern Shoppers
              </h2>

              <p>
                Online shopping has evolved dramatically over the past few years. What began as simple point-and-click purchasing has transformed into a complex ecosystem of product research, cross-platform price comparison, quality verification, and community-driven recommendations. At the center of this transformation sits an unlikely hero: the <strong className="text-foreground-900">Hipobuy Sheet</strong> — a structured spreadsheet system that has quietly revolutionized how informed buyers approach product discovery.
              </p>

              <p>
                If you have ever found yourself juggling fifteen browser tabs while trying to decide between three similar pairs of <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">shoes</a>, or scribbling product codes on sticky notes that inevitably disappear, you already understand the problem that Hipobuy Sheet solves. It brings order to chaos by centralizing product data — names, SKUs, sizes, colors, prices, seller ratings, and availability — into one clean, filterable, sortable document.
              </p>

              <div className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-6 my-8">
                <h3 className="text-base font-semibold text-foreground-900 mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-lightbulb-line text-sm"></i>
                  </span>
                  Key Takeaway
                </h3>
                <p className="text-sm text-foreground-600">
                  A well-organized Hipobuy Sheet can reduce product research time by up to 70% while dramatically improving the quality of your purchasing decisions through structured comparison data.
                </p>
              </div>

              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mt-10 mb-4">
                What Exactly Is a Hipobuy Sheet?
              </h2>

              <p>
                At its core, a <strong className="text-foreground-900">Hipobuy Sheet</strong> is a meticulously organized spreadsheet that catalogs products across multiple categories — from <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">hoodies and sweaters</a> to <a href="https://kaspreadsheet.com/search-921/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">bags</a>, <a href="https://kaspreadsheet.com/search-922/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">belts</a>, and <a href="https://kaspreadsheet.com/search-923/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">perfumes</a>. Think of it as your personal product database — a living document that grows with your shopping needs.
              </p>

              <p>
                Unlike static wish lists or browser bookmarks, a Hipobuy spreadsheet is dynamic. You can add columns for any attribute that matters to you: material composition, country of origin, estimated shipping time, discount eligibility, or even personal notes about fit and quality from previous purchases. Over time, this accumulated data becomes an invaluable reference that informs every future buying decision.
              </p>

              <div className="my-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Clean%20modern%20desk%20setup%20with%20laptop%20displaying%20organized%20colorful%20spreadsheet%20data%2C%20surrounded%20by%20fashion%20product%20samples%20including%20sneakers%20and%20fabric%20swatches%2C%20warm%20cream%20and%20beige%20ambient%20tones%2C%20soft%20natural%20window%20lighting%2C%20minimalist%20creative%20workspace%2C%20professional%20yet%20cozy%20atmosphere%2C%20high-end%20editorial%20photography%2C%20thoughtful%20composition%20with%20shallow%20depth%20of%20field%2C%20calm%20productive%20mood&width=1200&height=600&seq=guide-organized-spreadsheet-2026&orientation=landscape"
                  alt="Organized Hipobuy Sheet spreadsheet research workspace"
                  className="w-full rounded-lg"
                />
              </div>

              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mt-10 mb-4">
                The 14 Categories: Navigating Your Hipobuy Sheet
              </h2>

              <p>
                One of the standout features of the Hipobuy Sheet ecosystem is its comprehensive category structure. Products are organized into fourteen distinct sections, each accessible through dedicated navigation. Here is a breakdown of the categories and what you will find in each:
              </p>

              <div className="overflow-x-auto my-6">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b-2 border-background-200">
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Category</th>
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Typical Products</th>
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Research Focus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { cat: 'Shoes', products: 'Sneakers, boots, loafers, sandals', focus: 'Sizing accuracy, material quality, sole durability' },
                      { cat: 'Hoodies / Sweaters', products: 'Pullovers, zip-ups, knitwear', focus: 'Fabric weight, fit consistency, print quality' },
                      { cat: 'T-Shirts', products: 'Crew necks, v-necks, long sleeves', focus: 'Cotton grade, shrinkage, graphic durability' },
                      { cat: 'Jackets', products: 'Bombers, denim, puffer, windbreakers', focus: 'Insulation, weather resistance, stitching' },
                      { cat: 'Pants / Shorts', products: 'Jeans, chinos, cargo, sweatpants', focus: 'Waist accuracy, inseam length, fabric stretch' },
                      { cat: 'Headwear', products: 'Caps, beanies, bucket hats', focus: 'Fit adjustability, embroidery quality' },
                      { cat: 'Sets', products: 'Matching top-bottom combos, tracksuits', focus: 'Set consistency, color matching' },
                      { cat: 'Jersey', products: 'Sports jerseys, team apparel', focus: 'Authenticity markers, numbering quality' },
                      { cat: 'Accessories', products: 'Watches, jewelry, sunglasses', focus: 'Material verification, clasp durability' },
                      { cat: 'Bags', products: 'Backpacks, totes, crossbody bags', focus: 'Strap strength, zipper quality, capacity' },
                      { cat: 'Belts', products: 'Leather, canvas, woven belts', focus: 'Buckle material, leather grade, sizing' },
                      { cat: 'Perfumes', products: 'Eau de parfum, cologne, oils', focus: 'Longevity, sillage, batch consistency' },
                      { cat: 'Underwear', products: 'Boxers, briefs, socks', focus: 'Elastic durability, fabric breathability' },
                      { cat: 'Other Stuff', products: 'Home goods, tech accessories, misc', focus: 'General quality, value assessment' },
                    ].map((row) => (
                      <tr key={row.cat} className="border-b border-background-200/60 hover:bg-background-100/50 transition-colors">
                        <td className="py-3 px-4 font-medium text-foreground-900">{row.cat}</td>
                        <td className="py-3 px-4 text-foreground-600">{row.products}</td>
                        <td className="py-3 px-4 text-foreground-600">{row.focus}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p>
                Each category on the main platform features dedicated product listings, making it easy to jump directly to the section that interests you. For example, if you are in the market for new <a href="https://kaspreadsheet.com/search-914/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">jackets</a> or fresh <a href="https://kaspreadsheet.com/search-916/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">headwear</a>, the structured layout ensures you spend less time searching and more time comparing.
              </p>

              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mt-10 mb-4">
                How to Build Your First Hipobuy Spreadsheet
              </h2>

              <p>
                Getting started with a Hipobuy Sheet is remarkably straightforward, even if you have never used a spreadsheet for shopping research before. Follow these steps to create a foundation that will serve you for years:
              </p>

              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong className="text-foreground-900">Choose Your Platform:</strong> Google Sheets is the most popular choice due to its cloud accessibility and sharing features, but Excel and Notion also work well. Pick whatever you are most comfortable with.
                </li>
                <li>
                  <strong className="text-foreground-900">Set Up Your Columns:</strong> Start with the essentials — Product Name, Category, Price, Size Options, Color Variants, Seller Name, and URL. Add more columns as your research needs evolve.
                </li>
                <li>
                  <strong className="text-foreground-900">Browse and Populate:</strong> Visit the main platform and begin adding products from categories that interest you, such as <a href="https://kaspreadsheet.com/search-913/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">T-shirts</a> or <a href="https://kaspreadsheet.com/search-919/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">jerseys</a>.
                </li>
                <li>
                  <strong className="text-foreground-900">Apply Filters:</strong> Use spreadsheet filter functions to sort by price, category, or seller rating. This is where the power of a structured sheet truly shines.
                </li>
                <li>
                  <strong className="text-foreground-900">Update Regularly:</strong> Products come and go, prices fluctuate, and new items appear daily. Set aside ten minutes each week to refresh your sheet with the latest data.
                </li>
              </ol>

              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mt-10 mb-4">
                Hipobuy Sheet vs Traditional Shopping: A Comparison
              </h2>

              <div className="overflow-x-auto my-6">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b-2 border-background-200">
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Aspect</th>
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Traditional Research</th>
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Hipobuy Sheet Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { aspect: 'Product Discovery', traditional: 'Slow — manual browsing across sites', hipobuy: 'Fast — centralized category navigation' },
                      { aspect: 'Organization', traditional: 'Scattered — bookmarks and sticky notes', hipobuy: 'Structured — one sortable spreadsheet' },
                      { aspect: 'Comparison', traditional: 'Difficult — switching between tabs', hipobuy: 'Easy — side-by-side in one view' },
                      { aspect: 'Price Tracking', traditional: 'Memory-based or manual notes', hipobuy: 'Historical data in dedicated columns' },
                      { aspect: 'Research Time', traditional: 'Hours per purchase', hipobuy: 'Minutes with pre-organized data' },
                      { aspect: 'Collaboration', traditional: 'Screenshot sharing', hipobuy: 'Live spreadsheet sharing' },
                    ].map((row) => (
                      <tr key={row.aspect} className="border-b border-background-200/60 hover:bg-background-100/50 transition-colors">
                        <td className="py-3 px-4 font-medium text-foreground-900">{row.aspect}</td>
                        <td className="py-3 px-4 text-foreground-500">{row.traditional}</td>
                        <td className="py-3 px-4 text-accent-700 font-medium">{row.hipobuy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mt-10 mb-4">
                Safety and Smart Shopping Practices
              </h2>

              <p>
                While a Hipobuy Sheet dramatically improves research efficiency, smart shopping requires more than just organized data. Here are essential safety practices to incorporate into your workflow:
              </p>

              <ul className="list-disc pl-5 space-y-2">
                <li><strong className="text-foreground-900">Verify Seller Reputation:</strong> Always check seller ratings and review history before adding products to your sheet. A low price means nothing if the seller is unreliable.</li>
                <li><strong className="text-foreground-900">Cross-Reference Product Images:</strong> Use reverse image search to confirm that product photos are original and not stolen from other listings.</li>
                <li><strong className="text-foreground-900">Check Size Charts Carefully:</strong> Sizing varies dramatically across brands and regions. Always consult the specific size chart for each product and compare it against your own measurements.</li>
                <li><strong className="text-foreground-900">Document Everything:</strong> Save screenshots of product descriptions, prices at time of order, and any seller communications. Your Hipobuy Sheet is the perfect place to store these references.</li>
                <li><strong className="text-foreground-900">Start Small:</strong> When trying a new seller or category, begin with a single lower-cost item before committing to larger purchases.</li>
              </ul>

              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mt-10 mb-4">
                Advanced Hipobuy Sheet Techniques
              </h2>

              <p>
                Once you have mastered the basics, consider these advanced strategies to extract even more value from your spreadsheet:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                {[
                  { title: 'Conditional Formatting', desc: 'Set up color rules that automatically highlight the lowest price in each category, or flag products with below-average seller ratings.' },
                  { title: 'Pivot Tables', desc: 'Create summary views that show average prices by category, helping you spot trends and identify the best times to buy.' },
                  { title: 'Data Validation', desc: 'Use dropdown lists for consistent category naming and standardized size entries, preventing messy data.' },
                  { title: 'Import Functions', desc: 'Leverage spreadsheet import functions to pull live data when available, keeping your sheet current with minimal manual effort.' },
                ].map((tip) => (
                  <div key={tip.title} className="p-4 bg-background-100 rounded-lg">
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">{tip.title}</h4>
                    <p className="text-xs text-foreground-600">{tip.desc}</p>
                  </div>
                ))}
              </div>

              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mt-10 mb-4">
                Frequently Asked Questions
              </h2>

              <div className="space-y-4 my-6">
                {[
                  { q: 'Is Hipobuy Sheet free to use?', a: 'The spreadsheet methodology itself is completely free — you only need a spreadsheet application. The main platform at kaspreadsheet.com provides access to product listings and category navigation at no cost.' },
                  { q: 'How often is the product data updated?', a: 'Product availability and pricing on the main platform are refreshed regularly. For your personal Hipobuy Sheet, we recommend updating it at least once per week to capture new arrivals and price changes.' },
                  { q: 'Can I share my Hipobuy Sheet with friends?', a: 'Absolutely. If you are using Google Sheets, you can share view-only or edit access with anyone. Collaborative shopping research often leads to better discoveries.' },
                  { q: 'What is the difference between Hipobuy Sheet and other shopping tools?', a: 'Unlike closed-platform shopping apps, Hipobuy Sheet gives you complete control over your data structure. You decide which attributes matter and how to organize them.' },
                  { q: 'Which categories have the most products?', a: 'Shoes and clothing categories — including <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">shoes</a>, <a href="https://kaspreadsheet.com/search-913/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">T-shirts</a>, and <a href="https://kaspreadsheet.com/search-914/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">jackets</a> — tend to have the most extensive product listings.' },
                ].map((faq) => (
                  <details key={faq.q} className="bg-background-100 border border-background-200/70 rounded-lg group">
                    <summary className="px-5 py-4 text-sm font-medium text-foreground-900 cursor-pointer hover:text-primary-500 transition-colors list-none flex items-center justify-between">
                      {faq.q}
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform">
                        <i className="ri-arrow-down-s-line"></i>
                      </span>
                    </summary>
                    <p className="px-5 pb-4 text-sm text-foreground-600" dangerouslySetInnerHTML={{ __html: faq.a }}></p>
                  </details>
                ))}
              </div>

              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mt-10 mb-4">
                Getting Started Today
              </h2>

              <p>
                The best time to start building your <strong className="text-foreground-900">Hipobuy Sheet</strong> is right now. Begin by visiting the main platform, exploring the fourteen product categories — from <a href="https://kaspreadsheet.com/search-915/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">pants and shorts</a> to <a href="https://kaspreadsheet.com/search-920/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">accessories</a> and <a href="https://kaspreadsheet.com/search-924/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">other stuff</a> — and populating your first spreadsheet with products that catch your eye.
              </p>

              <p>
                Remember: a Hipobuy Sheet is not just a shopping list. It is a research tool, a price tracker, a quality reference, and a decision-making aid — all rolled into one elegantly simple format. The shoppers who invest time in building and maintaining their sheets consistently report faster, more confident purchasing decisions and fewer post-purchase regrets.
              </p>

              <div className="bg-primary-50 border border-primary-200/50 rounded-lg p-6 my-8 text-center">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">
                  Ready to Start Shopping Smarter?
                </h3>
                <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
                  Visit the main Hipobuy platform to explore all 14 product categories and begin building your personalized shopping spreadsheet today.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Hipobuy Sheet
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-external-link-line text-xs"></i>
                    </span>
                  </a>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-100 text-foreground-800 text-sm font-semibold rounded-full border border-background-200 hover:border-primary-300 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Buy It Now
                  </a>
                </div>
              </div>

            </div>
          </div>
        </article>

        <section className="bg-background-100 py-14 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-3">
              Explore More Resources
            </h3>
            <p className="text-sm text-foreground-600 mb-6">
              Return to the homepage to browse all product categories and discover more shopping research tools.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-foreground-900 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-xs"></i>
                </span>
                Back to Homepage
              </Link>
              <Link
                to="/hipobuy-sheet-safety"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-100 text-accent-700 text-sm font-semibold rounded-full hover:bg-accent-200 transition-all whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shield-check-line text-xs"></i>
                </span>
                Safety Guide
              </Link>
              <Link
                to="/hipobuy-sheet-comparison"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-secondary-100 text-secondary-700 text-sm font-semibold rounded-full hover:bg-secondary-200 transition-all whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-scales-3-line text-xs"></i>
                </span>
                Comparison
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}